import ArrowLeftOutlinedIcon from '@uala/icons/ArrowLeftOutlinedIcon';
import ArrowRightOutlinedIcon from '@uala/icons/ArrowRightOutlinedIcon';
import KeenSlider, { TOptionsEvents } from 'keen-slider';
import { useKeenSlider } from 'keen-slider/react';
import React, { forwardRef, useEffect } from 'react';

import {
  StyledArrowButton,
  StyledContainer,
  StyledTabsContainer,
  StyledTabsInnerContainer
} from './styles';
export interface TabsProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  selected: number;
  slidesPerView?: number;
  variant?: 'line' | 'point';
  mode?: 'snap' | 'free-snap' | 'free';
  center?: boolean;
  resetSlide?: boolean;
  showArrows?: boolean;
  onChange?(index: number): void;
  onMounted?(slider: KeenSlider): void;
  onCreated?(slider: KeenSlider): void;
  breakpoints?: {
    [key: string]: Omit<TOptionsEvents, 'breakpoints'>;
  };
}

const Tabs = forwardRef<HTMLDivElement, TabsProps>(
  (
    {
      children,
      selected = 0,
      onChange,
      onMounted,
      onCreated,
      breakpoints,
      variant = 'line',
      mode = 'free-snap',
      center = false,
      showArrows = false,
      resetSlide = false,
      slidesPerView = 3,
      ...rest
    },
    ref
  ) => {
    const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
      slidesPerView: slidesPerView,
      mode,
      centered: center,
      resetSlide: resetSlide,
      loop: false,
      mounted: (s) => {
        if (onMounted) onMounted(s);
      },
      created: (s) => {
        if (onCreated) onCreated(s);
      },
      breakpoints
    });

    useEffect(() => {
      if (slider) slider.moveToSlideRelative(selected);
    }, [selected, slider]);

    const handleOnSelect = (index) => {
      slider.moveToSlide(index);
      onChange(index);
    };

    const onNextSlide = () => {
      const currentPosition = selected;
      if (currentPosition < slider.details().size)
        handleOnSelect(currentPosition + 1);
    };

    const onPrevSlide = () => {
      const currentPosition = selected;
      if (currentPosition > 0 && currentPosition < slider.details().size)
        handleOnSelect(currentPosition - 1);
    };

    const disableLeftArrow = selected === 0;

    const disableRightArrow = slider && selected === slider.details().size - 1;

    return (
      <StyledContainer ref={ref}>
        {showArrows && (
          <StyledArrowButton
            disabled={disableLeftArrow}
            onClick={onPrevSlide}
            aria-label="anterior"
            id="left-arrow"
          >
            <ArrowLeftOutlinedIcon
              fontSize={25}
              color={disableLeftArrow ? '#bdbdbd' : '#3564FD'}
            />
          </StyledArrowButton>
        )}
        <StyledTabsContainer variant={variant} {...rest}>
          <StyledTabsInnerContainer ref={sliderRef}>
            {React.Children.map(children, function (child, index) {
              if (!React.isValidElement(child)) return;

              return React.cloneElement(child, {
                selected: selected === index,
                onClick: (event: React.MouseEvent) => {
                  event.preventDefault();
                  handleOnSelect(index);
                },
                variant
              });
            })}
          </StyledTabsInnerContainer>
        </StyledTabsContainer>
        {showArrows && (
          <StyledArrowButton
            disabled={disableRightArrow}
            onClick={onNextSlide}
            aria-label="siguiente"
            id="right-arrow"
          >
            <ArrowRightOutlinedIcon
              fontSize={25}
              color={disableRightArrow ? '#bdbdbd' : '#3564FD'}
            />
          </StyledArrowButton>
        )}
      </StyledContainer>
    );
  }
);

export default Tabs;
